import { useForm, usePage, Link } from '@inertiajs/react'
import { QRCodeSVG } from 'qrcode.react'
import AccountLayout from '@/layouts/AccountLayout'
import { useAccountPath } from '@/lib/useAccountPath'
import type { SharedProps } from '@/types'
import { useEffect } from 'react'
import type { FormEvent } from 'react'

interface MfaProps {
  enrolled: boolean
  pending_setup: boolean
  provisioning_uri: string | null
  manual_secret: string | null
  backup_codes_remaining: number
  backup_codes: string[] | null
}

export default function Mfa() {
  const props = usePage<{ props: SharedProps & MfaProps }>().props as unknown as SharedProps & MfaProps
  const { enrolled, pending_setup, provisioning_uri, manual_secret, backup_codes_remaining, backup_codes } = props
  const ap = useAccountPath()

  const enrollForm = useForm({ current_password: '' })
  const confirmForm = useForm({ code: '' })
  const disableForm = useForm({ current_password: '' })
  const regenForm = useForm({ current_password: '' })

  // The codes are already rendered from the live props above; Inertia also
  // caches page props in window.history, so Back/Forward would re-reveal these
  // one-time secrets without a server round-trip. Scrub them from the cached
  // history entry (best-effort, version-agnostic) so "shown only once" holds.
  useEffect(() => {
    if (!backup_codes || backup_codes.length === 0) return
    try {
      const scrub = (value: unknown): void => {
        if (!value || typeof value !== 'object') return
        const obj = value as Record<string, unknown>
        if ('backup_codes' in obj) obj.backup_codes = null
        Object.values(obj).forEach(scrub)
      }
      const state = window.history.state as unknown
      if (state && typeof state === 'object') {
        scrub(state)
        window.history.replaceState(state, '')
      }
    } catch {
      // Best-effort history hygiene; never block the reveal on it.
    }
  }, [backup_codes])

  function submitEnroll(e: FormEvent) {
    e.preventDefault()
    enrollForm.post(ap('/settings/mfa/enroll'))
  }
  function submitConfirm(e: FormEvent) {
    e.preventDefault()
    confirmForm.post(ap('/settings/mfa/confirm'))
  }
  function submitDisable(e: FormEvent) {
    e.preventDefault()
    disableForm.delete(ap('/settings/mfa'))
  }
  function submitRegen(e: FormEvent) {
    e.preventDefault()
    regenForm.post(ap('/settings/mfa/backup_codes'), {
      onSuccess: () => regenForm.reset('current_password'),
    })
  }

  return (
    <AccountLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <Link href={ap('/settings')} className="link link-hover text-sm text-base-content/60">
            ← Back to settings
          </Link>
          <h1 className="text-2xl font-bold mt-2">Two-factor authentication</h1>
          <p className="text-base-content/60 mt-1">
            Add a second step at login using an authenticator app.
          </p>
        </div>

        {/* One-time backup-code reveal, shown right after enrolling or regenerating. */}
        {backup_codes && backup_codes.length > 0 && (
          <div className="card bg-warning/10 border border-warning">
            <div className="card-body">
              <h2 className="card-title text-lg">Save your backup codes</h2>
              <p className="text-sm text-base-content/70">
                Each code works once if you lose access to your authenticator. This is the only
                time they're shown — store them somewhere safe.
              </p>
              <ul className="grid grid-cols-2 gap-2 font-mono text-sm mt-2">
                {backup_codes.map((code) => (
                  <li key={code} className="bg-base-100 rounded px-3 py-1.5 text-center">
                    {code}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* State 1 — enrolled: manage. */}
        {enrolled && (
          <div className="space-y-6">
            <div className="card bg-base-100 shadow">
              <div className="card-body">
                <div className="flex items-center gap-2">
                  <span className="badge badge-success">On</span>
                  <span className="text-sm text-base-content/70">
                    Two-factor authentication is protecting your account.
                  </span>
                </div>
                <p className="text-sm text-base-content/60 mt-2">
                  {backup_codes_remaining} backup{' '}
                  {backup_codes_remaining === 1 ? 'code' : 'codes'} remaining.
                </p>
              </div>
            </div>

            <div className="card bg-base-100 shadow">
              <div className="card-body">
                <h2 className="card-title text-lg">Regenerate backup codes</h2>
                <p className="text-sm text-base-content/60">
                  Generates a new set and invalidates the old ones. Enter your password to confirm.
                </p>
                <form onSubmit={submitRegen} className="flex items-end gap-3 mt-2">
                  <fieldset className="fieldset grow">
                    <legend className="fieldset-legend">Password</legend>
                    <input
                      type="password"
                      className="input w-full"
                      value={regenForm.data.current_password}
                      onChange={(e) => regenForm.setData('current_password', e.target.value)}
                      autoComplete="current-password"
                      required
                    />
                  </fieldset>
                  <button type="submit" className="btn btn-outline" disabled={regenForm.processing}>
                    Regenerate
                  </button>
                </form>
              </div>
            </div>

            <div className="card bg-base-100 shadow border border-error/30">
              <div className="card-body">
                <h2 className="card-title text-lg text-error">Turn off two-factor authentication</h2>
                <p className="text-sm text-base-content/60">
                  Your account will be protected by password only. Enter your password to confirm.
                </p>
                <form onSubmit={submitDisable} className="flex items-end gap-3 mt-2">
                  <fieldset className="fieldset grow">
                    <legend className="fieldset-legend">Password</legend>
                    <input
                      type="password"
                      className="input w-full"
                      value={disableForm.data.current_password}
                      onChange={(e) => disableForm.setData('current_password', e.target.value)}
                      autoComplete="current-password"
                      required
                    />
                  </fieldset>
                  <button type="submit" className="btn btn-error btn-outline" disabled={disableForm.processing}>
                    Turn off
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* State 2 — enrollment started, awaiting confirmation: show the QR. */}
        {!enrolled && pending_setup && (
          <div className="card bg-base-100 shadow">
            <div className="card-body">
              <h2 className="card-title text-lg">Scan the QR code</h2>
              <p className="text-sm text-base-content/60">
                Scan this with Google Authenticator, 1Password, Authy, or any TOTP app, then enter
                the 6-digit code it shows to finish.
              </p>

              {provisioning_uri && (
                <div className="flex justify-center my-4">
                  <div className="bg-white p-4 rounded-lg">
                    <QRCodeSVG value={provisioning_uri} size={180} />
                  </div>
                </div>
              )}

              {manual_secret && (
                <div className="text-center text-sm text-base-content/60 mb-2">
                  <p>Can't scan? Enter this key manually:</p>
                  <code className="font-mono text-xs break-all bg-base-200 rounded px-2 py-1 inline-block mt-1">
                    {manual_secret}
                  </code>
                </div>
              )}

              <form onSubmit={submitConfirm} className="space-y-3">
                <fieldset className="fieldset">
                  <legend className="fieldset-legend">Authentication code</legend>
                  <div className="flex justify-center">
                    {/* No w-full: daisyUI positions the digits with a letter-spacing tuned to
                        the otp's NATURAL width, so stretching the label desynchronises the
                        caret from the cell grid and clips each digit against its right
                        border. Centre the component rather than widening it. */}
                    <label className="otp otp-lg">
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <input
                      type="text"
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      maxLength={6}
                      pattern="[0-9]{6}"
                      value={confirmForm.data.code}
                      onChange={(e) => confirmForm.setData('code', e.target.value.replace(/\D/g, ''))}
                      required
                      autoFocus
                    />
                    </label>
                  </div>
                </fieldset>
                <button type="submit" className="btn btn-primary w-full" disabled={confirmForm.processing}>
                  {confirmForm.processing && <span className="loading loading-spinner loading-sm" />}
                  Turn on two-factor authentication
                </button>
              </form>
            </div>
          </div>
        )}

        {/* State 3 — not enrolled, no pending setup: offer to enable. */}
        {!enrolled && !pending_setup && (
          <div className="card bg-base-100 shadow">
            <div className="card-body">
              <div className="flex items-center gap-2">
                <span className="badge badge-ghost">Off</span>
                <span className="text-sm text-base-content/70">
                  Two-factor authentication is not set up.
                </span>
              </div>
              <form onSubmit={submitEnroll} className="mt-3 space-y-3">
                <fieldset className="fieldset max-w-sm">
                  <legend className="fieldset-legend">Confirm your password to start</legend>
                  <input
                    type="password"
                    className="input w-full"
                    value={enrollForm.data.current_password}
                    onChange={(e) => enrollForm.setData('current_password', e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </fieldset>
                <button type="submit" className="btn btn-primary" disabled={enrollForm.processing}>
                  Set up two-factor authentication
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </AccountLayout>
  )
}
